import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import { GygQueryOptions } from '@/src/views/components/master-pricing-list/types'
import { MenuTag, MenuTagGroup, MenuTagUpdate } from '@/src/types/apps/gygMenuTag'

interface MenuTagsInitialState {
  menuTags?: MenuTag[] | null
  menuTag: MenuTag | null
  menuTagGroups?: MenuTagGroup[] | null
  loading: boolean
  success: string | null
  error: string | null
  meta: { totalRows: number; pages: number } | null
}

const initialState: MenuTagsInitialState = {
  menuTags: null,
  menuTag: null,
  loading: false,
  success: null,
  error: null,
  meta: null
}

interface MenuTagsResponse {
  data: MenuTag[]
  meta: { totalRows: number; pages: number }
}

interface MenuTagResponse {
  data: MenuTag
}

interface MenuTagUpdateThunkPayload {
  id: string
  data: MenuTagUpdate
}

interface MenuTagCreateThunkPayload {
  data: MenuTagUpdate
}

interface MenuTagGroupsResponse {
  data: MenuTagGroup[]
}

export interface MenuTagsPayload {
  menuContainerProductId: number
  menuContainerProductPosPlu: number
  menuContainerPosPlu: number
  posMenus: number[]
  tags: string[]
}

export const updateMenuTag = createAsyncThunk<MenuTagResponse, MenuTagUpdateThunkPayload, Redux>(
  'gygMenuTags/updateMenuTag',
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`menu-tags/${id}`, { method: 'PATCH', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)
export const createMenuTag = createAsyncThunk<MenuTagResponse, MenuTagCreateThunkPayload, Redux>(
  'gygMenuTags/updateMenuTag',
  async ({ data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`menu-tags`, { method: 'POST', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const getMenuTags = createAsyncThunk<MenuTagsResponse, any, Redux>(
  'gygMenuTags/getAllMenuTags',
  async (gygQueryOptions, { extra }) => {
    const { data: responseData } = await extra.httpClient.get(`menu-tags`, { params: gygQueryOptions })

    return responseData
  }
)

export const getMenuTagGroups = createAsyncThunk<MenuTagGroupsResponse, GygQueryOptions, Redux>(
  'gygMenuTags/getAllMenuTagGroups',
  async (gygQueryOptions, { extra }) => {
    const { data: responseData } = await extra.httpClient.get(`menu-tag-groups`, { params: gygQueryOptions })

    return responseData
  }
)

export const saveMenuTags = createAsyncThunk<MenuTagsPayload, MenuTagsPayload[], Redux>(
  'gygMenuTags/saveMenuTag',
  async (menuTags, { extra }) => {
    const { data: responseData } = await extra.httpClient.put(`menu-container-product-tags/bulk`, menuTags)

    return responseData
  }
)

const gygMenuTagsSlice = createSlice({
  name: 'getAllMenuTags',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      .addCase(updateMenuTag.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateMenuTag.fulfilled, (state, action) => {
        state.loading = false
        state.success = 'menuTag updated successfully'
        state.menuTag = action.payload.data
      })
      .addCase(updateMenuTag.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to update menuTag'
      })

      .addCase(getMenuTags.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getMenuTags.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuTags = action.payload.data
        state.meta = action.payload.meta
      })
      .addCase(getMenuTags.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch MenuTags'
      })

      .addCase(getMenuTagGroups.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getMenuTagGroups.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuTagGroups = action.payload.data
      })
      .addCase(getMenuTagGroups.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu tag Groups'
      })
  }
})

export default gygMenuTagsSlice.reducer
